import { useState } from 'react';

const programs = [
  {
    id: 1,
    badge: '필수',
    badgeColor: 'bg-amber-100 text-amber-800',
    title: '기본 활동지원 역량교육',
    subtitle: '신규 활동지원사 입문 과정',
    duration: '40시간 · 5일',
    level: '입문',
    image: 'https://public.readdy.ai/ai/img_res/0457ab37e4f4a3170ad6d909a93f1701.jpg',
    topics: ['장애 이해 및 인권', '기본 활동지원 기술', '안전 관리', '이용자 소통법'],
    nextDate: '2026. 04. 07',
    seats: '잔여 8석',
  },
  {
    id: 2,
    badge: '인기',
    badgeColor: 'bg-rose-100 text-rose-700',
    title: '감정노동 회복 프로그램',
    subtitle: '번아웃 예방 및 심리 회복',
    duration: '16시간 · 2일',
    level: '중급',
    image: 'https://readdy.ai/api/search-image?query=Korean%20counseling%20and%20wellness%20group%20session%20for%20caregivers%2C%20peaceful%20calm%20interior%2C%20warm%20soft%20light%2C%20relaxed%20and%20healing%20atmosphere%2C%20small%20group%20of%20people%20sharing%20and%20connecting&width=600&height=380&seq=edu002&orientation=landscape',
    topics: ['감정노동 이해', '스트레스 관리', '자기돌봄 기술', '집단 심리치유'],
    nextDate: '2026. 04. 14',
    seats: '잔여 12석',
  },
  {
    id: 3,
    badge: '전문',
    badgeColor: 'bg-green-100 text-green-700',
    title: '전문 활동지원 심화과정',
    subtitle: '중증 장애인 전문 지원',
    duration: '32시간 · 4일',
    level: '고급',
    image: 'https://readdy.ai/api/search-image?query=professional%20advanced%20Korean%20caregiver%20training%20with%20hands%20on%20practice%2C%20medical%20and%20care%20skills%20demonstration%2C%20professional%20training%20center%2C%20warm%20educational%20environment&width=600&height=380&seq=edu003&orientation=landscape',
    topics: ['중증 장애 이해', '의료적 케어 기초', '보조기기 활용', '응급처치'],
    nextDate: '2026. 04. 21',
    seats: '잔여 5석',
  },
  {
    id: 4,
    badge: '온라인',
    badgeColor: 'bg-indigo-100 text-indigo-700',
    title: '법적 권리와 제도 이해',
    subtitle: '활동지원사 노동권 보호',
    duration: '8시간 · 자기주도',
    level: '온라인',
    image: 'https://readdy.ai/api/search-image?query=person%20studying%20online%20learning%20course%20about%20legal%20rights%20and%20welfare%20policy%2C%20laptop%20screen%20with%20Korean%20education%20content%2C%20cozy%20home%20study%20environment%2C%20warm%20desk%20lamp%20light&width=600&height=380&seq=edu004&orientation=landscape',
    topics: ['근로기준법 이해', '산재보험 활용', '부당처우 대처', '권리구제 절차'],
    nextDate: '상시 수강 가능',
    seats: '제한 없음',
  },
];

export default function EducationSection() {
  const [activeTab, setActiveTab] = useState<'all' | '입문' | '중급' | '고급' | '온라인'>('all');

  const filtered = activeTab === 'all' ? programs : programs.filter((p) => p.level === activeTab);

  return (
    <section id="education" className="py-28 bg-amber-50">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-200 text-amber-900 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
              <i className="ri-book-open-line"></i>
              EDUCATION PROGRAMS
            </div>
            <h2 className="text-5xl font-bold text-stone-800 mb-3">
              역량강화 교육 프로그램
            </h2>
            <p className="text-stone-500 text-lg">
              전문성을 높이고 더 나은 활동지원사로 성장하세요.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full border border-stone-200 p-1">
            {(['all', '입문', '중급', '고급', '온라인'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-stone-800 text-white'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                {tab === 'all' ? '전체' : tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((program) => (
            <div key={program.id} className="bg-white rounded-3xl overflow-hidden border border-stone-100 hover:shadow-md transition-shadow group">
              <div className="relative h-44 overflow-hidden">
                <img
                  src={program.image}
                  alt={program.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-3 left-3">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${program.badgeColor}`}>
                    {program.badge}
                  </span>
                </div>
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1">
                  <span className="text-stone-600 text-xs font-medium">{program.level}</span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-stone-400 text-xs mb-1">{program.subtitle}</p>
                <h3 className="font-bold text-stone-800 text-base mb-3 leading-snug">{program.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-stone-400 text-xs"></i>
                  </div>
                  <span className="text-stone-500 text-xs">{program.duration}</span>
                </div>
                <div className="space-y-1.5 mb-5">
                  {program.topics.map((t) => (
                    <div key={t} className="flex items-center gap-2">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-check-line text-amber-500 text-xs"></i>
                      </div>
                      <span className="text-stone-600 text-xs">{t}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-stone-100 pt-4 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-stone-400">다음 개강</p>
                      <p className="text-stone-700 text-xs font-semibold">{program.nextDate}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-stone-400">잔여 자리</p>
                      <p className="text-amber-600 text-xs font-semibold">{program.seats}</p>
                    </div>
                  </div>
                </div>
                <button className="w-full py-2.5 bg-stone-800 hover:bg-stone-900 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                  신청하기
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-3xl p-8 border border-stone-200">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: 'ri-calendar-check-line', num: '연 200회 이상', label: '교육 프로그램 운영' },
              { icon: 'ri-user-star-line', num: '3,500+', label: '연간 교육 수료자' },
              { icon: 'ri-medal-line', num: '92%', label: '교육 만족도' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-2xl mb-3">
                  <i className={`${stat.icon} text-amber-600 text-2xl`}></i>
                </div>
                <p className="text-3xl font-bold text-stone-800 mb-1">{stat.num}</p>
                <p className="text-stone-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
